import React, { useState, useEffect } from 'react';
import API from '../services/api';
import { useToast } from '../contexts/ToastContext';


interface MFASetupProps {
  onClose: () => void;
  onSuccess: () => void;
  allowClose?: boolean;
}

interface MFASetupData {
  secret: string;
  qrCode: string;
  manualEntryKey: string;
}

const MFASetup: React.FC<MFASetupProps> = ({ onClose, onSuccess, allowClose = true }) => {
  const { showToast } = useToast();
  const [step, setStep] = useState<number>(1);
  const [setupData, setSetupData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    initiateSetup();
  }, []);

  const initiateSetup = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await API.get('/api/mfa/setup');

      if (response.data.success) {
        setSetupData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to setup MFA');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await API.post('/api/mfa/enable', {
          secret: setupData?.secret,
          token: verificationCode
        });

      if (response.data.success) {
        setBackupCodes(response.data.data.backupCodes);
        setStep(3); // Move to backup codes display
      } else {
        setError(response.data.message || 'Invalid verification code');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const content = 'IPRI MFA Backup Codes\n\n' + backupCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ipri-mfa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    showToast('success', 'Backup codes copied to clipboard!');
  };

  const handleFinish = () => {
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Setup Two-Factor Authentication
          </h2>
          {allowClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              ✕
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Step 1: Scan QR Code */}
        {step === 1 && setupData && (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Step 1: Scan the QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
            </p>

            <div className="flex justify-center mb-4">
              <img src={setupData.qrCode} alt="QR Code" className="border-2 border-gray-300 dark:border-gray-600 rounded" />
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded mb-4">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                Can't scan? Enter this code manually:
              </p>
              <p className="text-sm font-mono bg-white dark:bg-gray-800 p-2 rounded border border-gray-300 dark:border-gray-600 break-all">
                {setupData.manualEntryKey}
              </p>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {/* Step 2: Verify Code */}
        {step === 2 && (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Step 2: Enter the 6-digit code from your authenticator app to verify
            </p>

            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 dark:border-gray-600 rounded mb-4"
              maxLength={6}
              autoFocus
            />

            <div className="flex gap-2">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                disabled={loading}
              >
                Back
              </button>
              <button
                onClick={verifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Backup Codes */}
        {step === 3 && backupCodes.length > 0 && (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Step 3: Save your backup codes. Each code can be used once if you lose access to your authenticator app.
            </p>

            <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mb-4">
              <p className="text-sm text-yellow-800 font-semibold mb-2">
                ⚠️ Important: Save these codes in a safe place!
              </p>
              <p className="text-xs text-yellow-700">
                You won't be able to see these codes again. Store them securely.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded mb-4 max-h-60 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div
                    key={index}
                    className="text-sm font-mono bg-white dark:bg-gray-800 p-2 rounded border border-gray-300 dark:border-gray-600 text-center"
                  >
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={copyBackupCodes}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
              >
                Copy Codes
              </button>
              <button
                onClick={downloadBackupCodes}
                className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors text-sm"
              >
                Download Codes
              </button>
            </div>

            <button
              onClick={handleFinish}
              className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
            >
              Finish Setup
            </button>
          </div>
        )}

        {loading && !setupData && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Loading...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MFASetup;
